import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";

import { Controller } from "../interface/controller.interface";
import { CurrentUserForUpdate } from "../persistence/dto/current-user-for-update.model";
import { FileType } from "../persistence/dto/file-type.enum";
import { HTTPMethod } from "../model/http-method.enum";
import { HTTPRequestBuilder } from "../model/http.request.model";
import { HTTPService } from "./http.service";
import { MimeType } from "../model/mime-type.enum";
import { MethodResult } from "../persistence/dto/method-result.model";
import { PagedQuery, PagedQueryBuilder } from "../persistence/dto/paged-query.model";
import { PagedQueryResult } from "../persistence/dto/paged-query-result.model";
import { RouteSequenceQuery } from "../persistence/dto/route-sequence-query.model";
import { TypeSystem } from "../support/type-system.support";
import {ResellerInfo, User} from "../persistence/dto/user.model";
import { UserFind } from "../persistence/dto/user-find.model";
import { UserForCreate } from "../persistence/dto/user-for-create.model";
import { orElse } from "../model/operator/or-else.operator";
import { ActivityHistoryDto, ActivityHistoryFilterDto } from "../core/models/activityHistoryDto.model";
import { usersLastestCampaignsReportFilter } from "../core/models/usersLastestCampaignsReportFilter.model";
import { UserLastestCampaignsDto } from "../persistence/dto/user-lastestcampaignsreport-model";
import { UsersChangesReportFilter } from "../persistence/dto/user-report-filter";
import { UsersInactiveReportFilter } from "../persistence/dto/user-report-filter";
import { UserCrmForUpdate } from "../persistence/dto/user-crm-for-update";
import {Country} from "../model/country.model";

/**
 * @description
 *  Servicio reflejo del controlador de usuarios del backend.
 *
 * @todo
 *  Faltan implementar los siguientes endpoints:
 *      - PATCH /api/users/current
 *
 * @see gigot-backend/Controllers/Users/UsersController.cs
 */

@Injectable({ providedIn: "root" })
export class UserService implements Controller {

	constructor(private httpService: HTTPService) { }

	public basePath(): string {
		return "api/users";
	}

    public nationalitiesPath(): string {
        return "api/nationality";
    }

	public createUser(user: UserForCreate): Observable<User> {
		return this.httpService.send<UserForCreate, User>(
			new HTTPRequestBuilder<UserForCreate>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath("")
				.payload(user)
				.build());
	}

	public currentUser(): Observable<User> {
		return this.httpService.send<typeof undefined, User>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath("current")
				.build());
	}

	public downloadFile(type: string, route: RouteSequenceQuery): Observable<any> {
		return this.httpService.send<typeof undefined, any>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(`files/${type}?routeSequenceQuery=${route}`)
				.build());
	}

	public downloadUsers<T>(pagedQuery: PagedQuery<T>): Observable<Blob> {
		return this.httpService.send<PagedQuery<T>, Blob>(
			new HTTPRequestBuilder<PagedQuery<T>>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath("exportUsers")
				.payload(pagedQuery)
				.responseType("blob")
				.build())
			.pipe(
				map((response) => new Blob([response], { type: MimeType.MICROSOFT_EXCEL_XLSX })));
	}

	public downloadCRMUsers<T>(pagedQuery: PagedQuery<T>): Observable<Blob> {
		return this.httpService.send<PagedQuery<T>, Blob>(
			new HTTPRequestBuilder<PagedQuery<T>>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath("exportCRMUsers")
				.payload(pagedQuery)
				.responseType("blob")
				.build())
			.pipe(
				map((response) => new Blob([response], { type: MimeType.MICROSOFT_EXCEL_XLSX })));
	}

	public downloadUsersAssignChanges(filters: any): Observable<Blob> {
		return this.httpService.send<UsersChangesReportFilter, Blob>(
			new HTTPRequestBuilder<UsersChangesReportFilter>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath("exportUsersAssignChanges")
				.payload(filters)
				.responseType("blob")
				.build())
			.pipe(
				map((response) => new Blob([response], { type: MimeType.MICROSOFT_EXCEL_XLSX })));
	}

	public downloadUsersInactive(filters: any): Observable<Blob> {
		return this.httpService.send<UsersInactiveReportFilter, Blob>(
			new HTTPRequestBuilder<UsersInactiveReportFilter>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath("exportUsersInactive")
				.payload(filters)
				.responseType("blob")
				.build())
			.pipe(
				map((response) => new Blob([response], { type: MimeType.MICROSOFT_EXCEL_XLSX })));
	}

	public findReferredUserByUserName<T>(userName: string): Observable<UserFind> {
		return this.findUsers(
			new PagedQueryBuilder<any>()
				.filter({ userName: userName,
                    isReferrerSearch: true})
				.pageNumber(1)
				.pageSize(1)
				.build())
			.pipe(
				map(pagedQueryResult => TypeSystem
					.orElse(pagedQueryResult.items[0], UserFind.empty())));
	}

	public findUsers<T>(pagedQuery: PagedQuery<T>): Observable<PagedQueryResult<UserFind>> {
		return this.httpService.send<PagedQuery<T>, PagedQueryResult<UserFind>>(
			new HTTPRequestBuilder<PagedQuery<T>>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath("find")
				.payload(pagedQuery)
				.build());
	}

	public findUsersLastestCampaignsReport<T>(reportFilter: usersLastestCampaignsReportFilter): Observable<UserLastestCampaignsDto[]> {
		return this.httpService.send<usersLastestCampaignsReportFilter, UserLastestCampaignsDto[]>(
			new HTTPRequestBuilder<usersLastestCampaignsReportFilter>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath("resellersReportData")
				.payload(reportFilter)
				.build());
	}

	public downloadUsersLastestCampaignsReport(reportFilter: usersLastestCampaignsReportFilter): Observable<Blob> {
		return this.httpService.send<usersLastestCampaignsReportFilter, Blob>(
			new HTTPRequestBuilder<usersLastestCampaignsReportFilter>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath("exportResellersReport")
				.payload(reportFilter)
				.responseType("blob")
				.build())
			.pipe(
				map((response) => new Blob([response], { type: MimeType.MICROSOFT_EXCEL_XLSX })));
	}

	public ownedUsers(): Observable<PagedQueryResult<User>> {
		return this.httpService.send<typeof undefined, PagedQueryResult<User>>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath("current/owned")
				.build());
	}

	public updateCurrentUser(user: CurrentUserForUpdate): Observable<typeof TypeSystem.EmptyObject> {
		return this.httpService.send<CurrentUserForUpdate, null>(
			new HTTPRequestBuilder<CurrentUserForUpdate>()
				.method(HTTPMethod.PUT)
				.controllerPath(this.basePath())
				.relativePath("current")
				.payload(user)
				.build())
			.pipe(map(() => TypeSystem.EmptyObject));
	}

	public updateCrmUser(user: UserCrmForUpdate): Observable<typeof TypeSystem.EmptyObject> {
		return this.httpService.send<UserCrmForUpdate, null>(
			new HTTPRequestBuilder<UserCrmForUpdate>()
				.method(HTTPMethod.PUT)
				.controllerPath(this.basePath())
				.relativePath("crm")
				.payload(user)
				.build())
			.pipe(map(() => TypeSystem.EmptyObject));
	}

	public updateFromFile(file: any, type: string): Observable<MethodResult> {
		return this.httpService.send<any, MethodResult>(
			new HTTPRequestBuilder<any>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath(`files/${type}`)
				.payload(file)
				.build());
	}

	public uploadFile(file: any, type: FileType): Observable<typeof TypeSystem.EmptyObject> {
		return this.httpService.send<any, null>(
			new HTTPRequestBuilder<any>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath(`current/files/${type}`)
				.payload(file)
				.build())
			.pipe(map(() => TypeSystem.EmptyObject));
	}

	public userByID(id: string): Observable<User> {
		return this.httpService.send<typeof undefined, User>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(id)
				.build());
	}

	public usersByCampaignLevel(campaignID: number, levelID: number): Observable<User[]> {
		return this.httpService.send<typeof undefined, User[]>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.GET)
				.controllerPath(this.basePath())
				.relativePath(`?campaignId=${campaignID}&levelId=${levelID}`)
				.build())
			.pipe(orElse([] as User[]));
	}

	public validatePhone(code: string): Observable<typeof TypeSystem.EmptyObject> {
		return this.httpService.send<typeof undefined, typeof TypeSystem.EmptyObject>(
			new HTTPRequestBuilder()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath(`current/validate-phone?code=${code}`)
				.build())
			.pipe(map(() => TypeSystem.EmptyObject));
	}

	public getOrderActivityHistory(filter: ActivityHistoryFilterDto): Observable<ActivityHistoryDto[]> {
		return this.httpService.send<ActivityHistoryFilterDto, ActivityHistoryDto[]>(
			new HTTPRequestBuilder<ActivityHistoryFilterDto>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath(`orderHistory`)
				.payload(filter)
				.build())
	}

	public getResellerActivityHistory(filter: ActivityHistoryFilterDto): Observable<ActivityHistoryDto[]> {
		return this.httpService.send<ActivityHistoryFilterDto, ActivityHistoryDto[]>(
			new HTTPRequestBuilder<ActivityHistoryFilterDto>()
				.method(HTTPMethod.POST)
				.controllerPath(this.basePath())
				.relativePath(`resellerActivityHistory`)
				.payload(filter)
				.build())
	}

    public getNationalities(): Observable<Country[]> {
        return this.httpService.send<typeof undefined, Country[]>(
            new HTTPRequestBuilder()
                .method(HTTPMethod.GET)
                .controllerPath(this.nationalitiesPath())
                .relativePath("list")
                .build());
    }

    public getCurrentResellerInfo(): Observable<ResellerInfo> {
        return this.httpService.send<typeof undefined, ResellerInfo>(
            new HTTPRequestBuilder()
                .method(HTTPMethod.GET)
                .controllerPath(this.basePath())
                .relativePath("currentResellerInfo")
                .build());
    }
}
